import { create } from "zustand";
import { getStoredToken, setStoredToken } from "../api/client";
import {
  getProfile,
  login as apiLogin,
  logout as apiLogout,
  refreshToken,
  verifyOtp,
} from "../api/authPatient";
import type { PatientUser } from "../api/authPatient";
import { listMesEnfants, type EnfantSummary } from "../api/liaisonParentEnfant";

let otpLoginInProgress = false;

export type Patient = {
  id?: string;
  email?: string;
  nip?: string;
  nom?: string;
  prenom?: string;
  dateNaissance?: string;
  type?: string;
  statut?: string;
};

/** Profil actif dans l'espace patient : le parent lui-même, ou un profil enfant rattaché. */
export type ActiveProfil = { nip: string; nom: string; prenom: string; isEnfant: boolean };

export type SignInCredentials = { nip: string };

function toPatient(user: PatientUser | null): Patient | null {
  if (!user) return null;
  const id = typeof user.id === "string" ? user.id.trim() : "";
  const email = typeof user.email === "string" ? user.email.trim() : "";
  const nip = typeof user.nip === "string" ? user.nip.trim() : "";
  const nom = typeof user.nom === "string" ? user.nom.trim() : "";
  const prenom = typeof user.prenom === "string" ? user.prenom.trim() : "";
  const dateNaissance = typeof user.date_naissance === "string" ? user.date_naissance.trim() : "";
  const type = typeof user.type === "string" ? user.type.trim() : "";
  const statut = typeof user.statut === "string" ? user.statut.trim() : "";

  if (!id && !email && !nip && !nom && !prenom) return null;
  return {
    id: id || undefined,
    email: email || undefined,
    nip: nip || undefined,
    nom: nom || undefined,
    prenom: prenom || undefined,
    dateNaissance: dateNaissance || undefined,
    type: type || undefined,
    statut: statut || undefined,
  };
}

/** Mappe la réponse `GET /auth/patient/profile` vers l’objet `Patient` du store (ex. après mise à jour profil). */
export function patientFromUser(user: PatientUser | null): Patient | null {
  return toPatient(user);
}

export type SignInResult =
  | { ok: true; requiresOtp: true; otpRequestId: string; userHint?: string }
  | { ok: false; message: string };

export type ConfirmOtpResult =
  | { ok: true }
  | { ok: false; message: string };

type AuthState = {
  patient: Patient | null;
  isLoading: boolean;
  enfants: EnfantSummary[];
  activeProfil: ActiveProfil | null;
  setPatient: (patient: Patient | null) => void;
  setActiveProfil: (profil: ActiveProfil) => void;
  refreshEnfants: () => Promise<void>;
  signIn: (credentials: SignInCredentials) => Promise<SignInResult>;
  confirmLoginOtp: (otpRequestId: string, code: string) => Promise<ConfirmOtpResult>;
  signOut: () => Promise<void>;
  clearAuth: () => void;
  initAuth: () => Promise<void>;
};

export const useAuthPatientStore = create<AuthState>()((set, get) => ({
  patient: null,
  isLoading: true,
  enfants: [],
  activeProfil: null,

  setPatient: (patient) => set({ patient }),

  setActiveProfil: (profil) => set({ activeProfil: profil }),

  refreshEnfants: async () => {
    const res = await listMesEnfants();
    if (!res.ok) return;
    const enfants = res.data.map((item) => item.enfant);
    set({ enfants });
    const current = get().activeProfil;
    if (current?.isEnfant && !enfants.some((e) => e.nip === current.nip)) {
      const self = get().patient;
      if (self?.nip) {
        set({ activeProfil: { nip: self.nip, nom: self.nom ?? "", prenom: self.prenom ?? "", isEnfant: false } });
      }
    }
  },

  signIn: async (credentials) => {
    const result = await apiLogin(credentials.nip);
    if (!result.ok) return { ok: false, message: result.message };
    return {
      ok: true,
      requiresOtp: true,
      otpRequestId: result.data.otpRequestId,
      userHint: result.data.userHint,
    };
  },

  confirmLoginOtp: async (otpRequestId, code) => {
    otpLoginInProgress = true;
    set({ isLoading: true });
    try {
      const result = await verifyOtp(otpRequestId, code);
      if (!result.ok) return { ok: false, message: result.message };
      const p = result.data?.user ? toPatient(result.data.user) : null;
      if (p) {
        set({
          patient: p,
          activeProfil: p.nip ? { nip: p.nip, nom: p.nom ?? "", prenom: p.prenom ?? "", isEnfant: false } : null,
        });
        void get().refreshEnfants();
      }
      return { ok: true };
    } finally {
      otpLoginInProgress = false;
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    await apiLogout();
    set({ patient: null, enfants: [], activeProfil: null });
  },

  clearAuth: () => {
    setStoredToken(null);
    set({ patient: null, enfants: [], activeProfil: null, isLoading: false });
  },

  initAuth: async () => {
    set({ isLoading: true });
    try {
      const tokenAtStart = getStoredToken();
      const refresh = await refreshToken();
      if (!refresh.ok) {
        if (!getStoredToken() && !tokenAtStart) {
          setStoredToken(null);
          set({ patient: null });
        }
        return;
      }

      const profileRes = await getProfile({ silent401: true });
      if (profileRes.ok) {
        const p = toPatient(profileRes.data);
        set({
          patient: p,
          activeProfil: p?.nip ? { nip: p.nip, nom: p.nom ?? "", prenom: p.prenom ?? "", isEnfant: false } : null,
        });
        if (p) void get().refreshEnfants();
      } else if (!getStoredToken() && !tokenAtStart) {
        set({ patient: null });
      }
    } finally {
      if (!otpLoginInProgress) set({ isLoading: false });
    }
  },
}));

export function usePatientAuth() {
  const patient = useAuthPatientStore((s) => s.patient);
  const isLoading = useAuthPatientStore((s) => s.isLoading);
  const setPatient = useAuthPatientStore((s) => s.setPatient);
  const signIn = useAuthPatientStore((s) => s.signIn);
  const confirmLoginOtp = useAuthPatientStore((s) => s.confirmLoginOtp);
  const signOut = useAuthPatientStore((s) => s.signOut);
  const enfants = useAuthPatientStore((s) => s.enfants);
  const activeProfil = useAuthPatientStore((s) => s.activeProfil);
  const setActiveProfil = useAuthPatientStore((s) => s.setActiveProfil);
  const refreshEnfants = useAuthPatientStore((s) => s.refreshEnfants);

  return {
    patient,
    setPatient,
    isAuthenticated: !isLoading && !!patient,
    isLoading,
    signIn,
    confirmLoginOtp,
    signOut,
    enfants,
    activeProfil,
    setActiveProfil,
    refreshEnfants,
  };
}
